/**
 * Modal — web port of RN's Modal component.
 *
 * Renders a fixed-position blurred backdrop with a slide-up card. Backdrop
 * tap closes; ESC closes; body scroll is locked while open.
 */

import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';
import './Modal.css';

interface ModalProps {
  visible: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
}

export const Modal = ({
  visible,
  onClose,
  title,
  children,
  footer,
}: ModalProps) => {
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [visible, onClose]);

  if (!visible) return null;

  return createPortal(
    <div
      className="wg-modal-backdrop"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="wg-modal-card" role="dialog" aria-modal="true">
        {title !== undefined && (
          <div className="wg-modal-header">
            <h3 className="wg-modal-title">{title}</h3>
            <button
              type="button"
              className="wg-modal-close"
              onClick={onClose}
              aria-label="Close"
            >
              <Icon name="close" size={18} color="#fff" />
            </button>
          </div>
        )}
        <div className="wg-modal-content">{children}</div>
        {footer ? <div className="wg-modal-footer">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
